export const DEFAULT_FISCAL_YEAR_START_MONTH = 4;
export const DEFAULT_PERIOD_MODE = "full_year";
export const PERIOD_MODE_TILL_LATEST = "till_latest_transaction";

export function fmtDate(value) {
  if (!value) return "";
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function parseDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value || "").trim();
  if (!text) return null;
  const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const dt = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(text);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function normalizeFiscalYearStartMonth(value) {
  const month = Number(value);
  return Number.isInteger(month) && month >= 1 && month <= 12
    ? month
    : DEFAULT_FISCAL_YEAR_START_MONTH;
}

export function normalizeDefaultPeriodMode(value) {
  return String(value || "").trim().toLowerCase() === PERIOD_MODE_TILL_LATEST
    ? PERIOD_MODE_TILL_LATEST
    : DEFAULT_PERIOD_MODE;
}

export function fiscalYearRange({
  offset = 0,
  fiscalYearStartMonth = DEFAULT_FISCAL_YEAR_START_MONTH,
  today = new Date(),
} = {}) {
  const startMonthIndex = normalizeFiscalYearStartMonth(fiscalYearStartMonth) - 1;
  const refDate = parseDate(today) || new Date();
  const baseYear = refDate.getMonth() >= startMonthIndex ? refDate.getFullYear() : refDate.getFullYear() - 1;
  const startYear = baseYear + Number(offset || 0);
  return {
    from: fmtDate(new Date(startYear, startMonthIndex, 1)),
    to: fmtDate(new Date(startYear + 1, startMonthIndex, 0)),
  };
}

export function currentQuarterRange(today = new Date()) {
  const refDate = parseDate(today) || new Date();
  const month = refDate.getMonth();
  const quarterStartMonth = month >= 3 && month <= 5 ? 3 : month >= 6 && month <= 8 ? 6 : month >= 9 && month <= 11 ? 9 : 0;
  return {
    from: fmtDate(new Date(refDate.getFullYear(), quarterStartMonth, 1)),
    to: fmtDate(new Date(refDate.getFullYear(), quarterStartMonth + 3, 0)),
  };
}

export function lastTwelveMonthsRange(today = new Date()) {
  const refDate = parseDate(today) || new Date();
  return {
    from: fmtDate(new Date(refDate.getFullYear(), refDate.getMonth() - 11, 1)),
    to: fmtDate(new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0)),
  };
}

export function clampRangeToEndDate(range, endDate) {
  const from = parseDate(range?.from);
  const to = parseDate(range?.to);
  const end = parseDate(endDate);
  if (!from || !to || !end || end < from || end > to) {
    return { from: range?.from || "", to: range?.to || "" };
  }
  return { from: fmtDate(from), to: fmtDate(end) };
}

export function resolveDashboardDefaultRange({
  fiscalYearStartMonth = DEFAULT_FISCAL_YEAR_START_MONTH,
  defaultPeriodMode = DEFAULT_PERIOD_MODE,
  latestTransactionDate = "",
  today = new Date(),
} = {}) {
  const range = fiscalYearRange({ fiscalYearStartMonth, today });
  if (normalizeDefaultPeriodMode(defaultPeriodMode) !== PERIOD_MODE_TILL_LATEST) {
    return range;
  }
  return clampRangeToEndDate(range, latestTransactionDate || today);
}

export function rangeForPreset(preset, options = {}) {
  if (preset === "last_fy") return fiscalYearRange({ ...options, offset: -1 });
  if (preset === "last_12m") return lastTwelveMonthsRange(options.today);
  if (preset === "this_quarter") return currentQuarterRange(options.today);
  return resolveDashboardDefaultRange(options);
}

export function shiftYear(value, years) {
  const dt = parseDate(value);
  if (!dt) return "";
  return fmtDate(new Date(dt.getFullYear() + Number(years || 0), dt.getMonth(), dt.getDate()));
}

export function fiscalYearLabel(from, to) {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return "Custom Period";
  return `FY ${start.getFullYear()}-${String(end.getFullYear()).slice(-2)}`;
}
